import GUI from 'lil-gui';
import adapter from 'webrtc-adapter';
import Time from './Utils/Time';
import Sizes from './Utils/Sizes';
import Resources from './Resources';

import Camera from './Camera';
import { autoInjectable, container, singleton } from 'tsyringe';
import Renderer from './Components/Renderer';
import VrScene, { Rooms } from './Components/Three/VrScene';
import { EventDispatcher, Scene } from 'three';
import AxiosHttpClient from './Network/AxiosHttpClient';
import 'material-icons/iconfont/material-icons.css';
import IntersectionContainer from './Components/Controllers/IntersectionContainer';

import LobbyScene from './Scenes/LobbyScene/LobbyScene';
import ColyseusClient from './Network/ColyseusClient';
import PrivateRoomScene from './Scenes/PrivateRoom/PrivateRoomScene';
import App from './react/app';
import React from 'react';
import ReactDOM from 'react-dom/client';
import RedirectService from './RedirectService';
import SchoolLobby from './Scenes/SchoolLobby/SchoolLobby';
import UserService from './Components/UserService';
import DemoService from './DemoService';
import { JanusClient } from './react/Janus/rx/JanusClient';
import Config from '../Config';
import { InteractionManager } from './Components/Controllers/InteractionManager';
import XrControllers from './Components/Controllers/XrControllers';
import HMRManager from './Utils/HMRManager';
import ExperimentsRoom from './Scenes/experiments/ExperimentsRoom';
import FirebaseClient from './Network/Firebase/FirebaseClient'; // ← DODAJ TO

@singleton()
@autoInjectable()
export default class Application extends EventDispatcher {
    private $canvas: HTMLCanvasElement;

    private config: {
        selectedScene?: string;
        debug?: boolean;
    };

    private debug: any;

    public scene: VrScene | Scene = new Scene();

    public isReady = false;

    public janusCommunicationService: JanusClient | null = null;

    public interactionManager: InteractionManager;

    private hmrManager: HMRManager;

    public constructor(
        public httpClient?: AxiosHttpClient,
        public renderer?: Renderer,
        public time?: Time,
        public sizes?: Sizes,
        public resources?: Resources,
        public camera?: Camera,
        public intersectionContainer?: IntersectionContainer,
        public colyseusClient?: ColyseusClient,
        public redirectService?: RedirectService,
        public demoService?: DemoService,
        public globalConfig?: Config,
    ) {
        super();

        this.globalConfig.skipLegacyIntersection = true;
        window.app = this;
        window['adapter'] = adapter;

        this.hmrManager = new HMRManager();
        this.hmrManager.init();
        window['hmr'] = this.hmrManager;
    }

    public async init(options: { $canvas?: HTMLCanvasElement; domContainer: HTMLDivElement }) {
        if (window.skipObtainToken) {
            const fakeRes = {
                data: {
                    token: localStorage.getItem('token'),
                },
            };
            await this.httpClient.setInterceptors(fakeRes);

            this.initializeComponents(options);
        } else {
            await this.obtainToken(options);
        }

        this.resources.on('ready', () => {
            if (!this.isReady) {
                this.onReady();
            }
        });

        this.resources.on('progress', this.onProgress);
    }

    public initializeComponents(options) {
        this.resources.start();
        this.$canvas = options.$canvas;
        this.$canvas.oncontextmenu = () => false;
        this.renderer.init(options.$canvas);
        this.camera.init();
        this.setupInteractionManager();

        this.interactionManager.setXrControllers(container.resolve(XrControllers));
        this.setConfig();
    }

    private setupInteractionManager() {
        this.interactionManager = container.resolve(InteractionManager);

        this.interactionManager.setRenderer(this.renderer);

        this.renderer.time.on('tick', (time) => {
            this.interactionManager.update();
        });
    }

    public async obtainToken(options) {
        try {
            const tokenRes = await this.httpClient.obtainToken();
            await this.httpClient.setInterceptors(tokenRes);

            this.initializeComponents(options);
        } catch (e) {
            console.error(e);
        }
    }

    public onProgress = (percent: any) => {
        if (percent != 100) {
            document.getElementById('progress-bar-value').innerText = 'Progress... ' + percent.toString() + '/100%';
        } else {
            document.getElementById('view-progress-bar').classList.add('hidden');
        }

        console.log(`progress ${percent}/100`);
    };

    private reactInit() {
        const rootElement = document.getElementById('app');
        const root = ReactDOM.createRoot(rootElement);

        root.render(React.createElement(App));
    }

    public log(text: string) {
        console.log(text);
    }

    public onReady = async () => {
        console.log('🚀 Application: onReady started');

        this.isReady = true;
        const userService = container.resolve(UserService);

        try {
            if (this.redirectService.getCourseId() === '1953308216667070') {
                this.demoService.setDemoMode(true);
            } else {
                this.demoService.setDemoMode(false);
            }
        } catch (e) {
            console.error('Demo service error', e);
        }

        if (this.demoService.isDemo) {
            this.resources.items.user.is_school_member = true;
            this.resources.items.user.school_id = this.resources.items.user.id;
            this.resources.items.user.is_school_teacher = true;

            userService.is_school_teacher = true;
            userService.school_id = this.resources.items.user.id;
            userService.is_school_member = true;
        }

        await this.resources.whenReady;

        console.log('🚀 Application: Initializing Firebase...');
        try {
            const firebaseClient = container.resolve(FirebaseClient);
            await firebaseClient.initialize();
            console.log('✅ Application: Firebase initialized');

            console.log('🚀 Application: Initializing Firebase-dependent components...');
            await this.initializeFirebaseDependentComponents();
            console.log('✅ Application: Firebase-dependent components initialized');

        } catch (error) {
            console.error('❌ Application: Firebase initialization failed, continuing without Firebase', error);
        }

        console.log('🚀 Application: Initializing React UI...');
        this.reactInit();
        console.log('✅ Application: React UI initialized');

        console.log('🚀 Application: Initializing Janus...');
        await this.janusInit();
        console.log('✅ Application: Janus initialized');

        console.log('🚀 Application: Initializing scene...');
        await this.sceneInit(new LobbyScene(), this.resources.items.user.id + 4, true, false);

        if (this.redirectService.isSchoolRedirect() && userService.is_school_member) {
            setTimeout(() => {
                (this.scene as VrScene).changeScene(new SchoolLobby(), this.resources.items.user.school_id, true);
            }, 1);

            this.redirectService.removeRedirectFlag();
        } else {
            setTimeout(() => {
                (this.scene as VrScene).changeScene(new LobbyScene(), this.resources.items.user.id + 4, true);
            }, 1);

            this.redirectService.removeRedirectFlag();
        }

        this.setExitLessonButton();
        this.setDebug();

        console.log('✅ Application: Initialization complete!');
    };


    private async initializeFirebaseDependentComponents(): Promise<void> {
        try {
            // Dispatch event dla komponentów które nasłuchują
            (this as any).dispatchEvent({ type: 'firebase-ready' });

            console.log('✅ Firebase-dependent components notified');
        } catch (error) {
            console.error('❌ Error initializing Firebase-dependent components:', error);
        }
    }

    private setExitLessonButton() {
        document.querySelector('.exit-lesson-button').addEventListener('click', () => {
            document.querySelector('.iframe-wrapper').classList.remove('active');
            document.querySelector('.main-iframe').setAttribute('src', '');
        });
    }

    private async sceneInit(scene: VrScene, roomId: number, multiplayer: boolean, janus: boolean) {
        this.scene = scene;

        //@ts-ignore
        window.app.scene = this.scene;

        this.scene.add(this.camera.cameraGroup);

        this.renderer.startRenderLoop(this.scene, this.camera.instance);

        await (this.scene as VrScene).joinRoom(roomId, multiplayer, null, janus);

        (this.scene as VrScene).start();
    }

    private async janusInit() {
        this.janusCommunicationService = container.resolve(JanusClient);

        await this.janusCommunicationService.connect();
    }

    public changeScenePrivateRoom(roomId: number) {
        (this.scene as VrScene).changeScene(new PrivateRoomScene(), roomId, true);
    }

    private setConfig() {
        this.config = {};
        this.config.debug = /#debug/.test(window.location.href);
        const urlParams = new URLSearchParams(window.location.search);

        this.config.selectedScene = urlParams.get('scene');
    }

    private setDebug() {
        if (this.config.debug) {
            this.debug = new GUI({ width: 300 });

            // if (this.config.selectedScene !== null) {
            //     const sceneExists =
            //         this.sceneManager
            //             .getSceneNames()
            //             .filter(
            //                 (sceneName: string) =>
            //                     sceneName === this.config.selectedScene,
            //             ).length > 0;
            //     if (!sceneExists) {
            //         console.error(
            //             `Scene ${this.config.selectedScene} does not exist`,
            //         );
            //         return;
            //     }
            //
            //     this.changeScene(this.config.selectedScene);
            // }

            // const myObject = {
            //     scene: this.scene.name,
            // };

            // this.debug
            //     .add(myObject, 'scene', this.sceneManager.getSceneNames())
            //     .name('Change scene')
            //     .onChange((value) => {
            //         this.changeScene(value);
            //     });
        }
    }
}
